import { getImageWithFocalPoints } from '@disco/api/imgixClient'
import { SHOWCASES } from '@disco/constants/FeatureTypes'
import { pick } from '@disco/utils/array'
import { useComposerInsteadOfArtist } from '@disco/utils/composerInsteadOfArtist'
import { getRawContentFromString } from '@disco/content-editor/editor-utils'
import { blobTobase64 } from '@disco/utils/convertImage'
import {
  isSupportedImageFormat,
  SUPPORTED_IMAGE_ACCEPT,
  unsupportedImageFormatMessage,
} from '@disco/utils/imageFormats'
import { parseServerError } from '@disco/utils/captureError'
import { pushUINotification } from '@disco/utils/notifications'
import isEqual from 'lodash/isEqual'

import React, { useEffect, useState } from 'react'
import { useDispatch } from 'hooks/use-redux'
import type { Artist } from 'types/artist'
import type { ClientLibraryArtist } from 'types/client-library'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { updateArtistImage } from '@disco/state-management/actions/artist-actions'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import Row from 'components/common/row'
import ImageRecommendationsModal from 'components/modals/image-recommendations-modal'
import ImageRecommendationsButton from 'components/modals/image-recommendations-modal/button'
import PlaylistPresentationSocialLinks from 'components/modals/playlist-presentation-social-links'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Checkbox from 'components/ui/checkbox'
import CroppingTool from 'components/ui/cropping-tool'
import FileSelector from 'components/ui/file-selector'
import Heading from 'components/ui/heading'
import HTMLEditor from 'components/ui/html-editor'
import Label from 'components/ui/label'
import useLocalStorage from 'hooks/use-local-storage'
type Props = {
  artist: Artist | ClientLibraryArtist
  onCancel: () => void
  onSubmit: (data: Partial<Artist & ClientLibraryArtist>) => Promise<any>
}

type CroppingToolOptions = {
  image: string | ArrayBuffer | null
  onClose: () => void
  onApply: (args: {
    base64Image: string
    imgixOptions: Record<string, any>
  }) => void
}

const ArtistProfile = ({
  artist,
  onCancel,
  onSubmit,
}: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const showcasesEnabled: boolean = useFeatureEnabled(SHOWCASES)
  const typeData = useComposerInsteadOfArtist()
  const [openLinksModal, setOpenLinksModal] = useState(false)
  const [formState, setFormState] = useState<
    Partial<Artist & ClientLibraryArtist>
  >(
    pick(artist, [
      'bio_editor',
      'bio_html',
      'social_links',
      'social_links_aggregator',
      'focal_points',
    ]),
  )
  const [image, setImage] = useState<File | null | undefined>()
  const [imagePreview, setImagePreview] = useState<string | null | undefined>()
  const [croppingToolOptions, setCroppingToolOptions] =
    useState<CroppingToolOptions | null>(null)
  const [showImageRecommendations, setShowImageRecommendations] =
    useState(false)
  const [saving, setSaving] = useState(false)
  const [dismissedImageRecommendations, setDismissedImageRecommendations] =
    useLocalStorage<Date | null | undefined>(`image_recommendations`, null)
  let artistImage = imagePreview

  if (!imagePreview && !artist.loading && artist.image_url) {
    artistImage = getImageWithFocalPoints({
      image: artist.image_url,
      params: {
        focal_points: artist.focal_points,
      },
    })
  }

  useEffect(() => {
    setFormState(
      pick(artist, [
        'bio_editor',
        'bio_html',
        'social_links',
        'social_links_aggregator',
        'focal_points',
      ]),
    )
  }, [artist])

  const checkImageRecommendations = () => {
    if (!dismissedImageRecommendations && !artistImage) {
      setShowImageRecommendations(true)
      setDismissedImageRecommendations(new Date())
      return false
    }
  }

  const handleImageChange = (files: File[] | null | undefined) => {
    const cancelDrop = checkImageRecommendations() === false
    if (cancelDrop) return
    const file = files ? files[0] : null

    if (!file) {
      setFormState(state => ({ ...state, focal_points: null }))
      setImagePreview(null)
      return setImage(null)
    }

    // the server validates images with Pillow, so formats it can't decode
    // (.svg, .heic) have to be rejected before the artist is saved
    if (!isSupportedImageFormat(file)) {
      return pushUINotification({
        message: unsupportedImageFormatMessage(file),
        level: 'error',
      })
    }

    ;(async () => {
      const base64Image = await blobTobase64(file)
      setImage(file)
      setCroppingToolOptions({
        image: base64Image,
        onClose: () => {
          setCroppingToolOptions(null)
        },
        onApply: async ({ base64Image: imagePreview, imgixOptions }) => {
          setImagePreview(imagePreview)
          const focal_points = {
            focal_point_x: imgixOptions['fp-x'],
            focal_point_y: imgixOptions['fp-y'],
            focal_point_z: imgixOptions['fp-z'],
          }
          setFormState(state => ({ ...state, focal_points }))
          setCroppingToolOptions(null)
        },
      })
    })()
  }

  const handleSubmit = async (event: React.SyntheticEvent<any>) => {
    event.preventDefault()
    setSaving(true)

    if (formState.bio_html !== artist.bio_html) {
      dispatch(sendMixpanelEvent('Manage Artist modal - Update artist bio'))
    }

    if (
      !isEqual(formState.social_links, artist.social_links) ||
      !isEqual(
        formState.social_links_aggregator,
        artist.social_links_aggregator,
      )
    ) {
      dispatch(sendMixpanelEvent('Manage Artist modal - Manage social links'))
    }

    // updateImage=null when we want to delete an image
    if (image !== undefined) {
      try {
        await dispatch(updateArtistImage(artist.id, image))
      } catch (error) {
        setSaving(false)
        return pushUINotification({
          message: parseServerError(error?.body, {
            fallback:
              'Sorry! A server error prevented this image from saving. Please try again, and if it fails, let us know.',
          }),
          level: 'error',
        })
      }
      dispatch(sendMixpanelEvent('Manage Artist modal - Update header image'))
    }

    await onSubmit(formState)
    setSaving(false)
    onCancel()
  }

  const toggleIsFeatured = (_, value: boolean) => {
    onSubmit({
      is_featured: value,
    })
  }

  return (
    <>
      <Row columnsSize={[12]} marginBottom="small">
        <div>
          <Label caption="Artist image">
            <ImageRecommendationsButton
              onClick={() => setShowImageRecommendations(true)}
            />
          </Label>
          <FileSelector
            accept={SUPPORTED_IMAGE_ACCEPT}
            onClick={checkImageRecommendations}
            type="image"
            height={345}
            image={artistImage}
            previewStyle={{
              backgroundPosition: '50% 30%',
            }}
            onDrop={handleImageChange}
            caption={['Artist image (landscape, minimum 2000 px wide)']}
            dnd
          />
          {typeof croppingToolOptions?.image === 'string' && (
            <CroppingTool
              src={croppingToolOptions.image}
              overlayStyle={{
                width: '550px',
                height: '353px',
              }}
              onClose={croppingToolOptions.onClose}
              onApply={croppingToolOptions.onApply}
              withImageRecommendations
            />
          )}
        </div>
      </Row>

      {artist.tracks !== undefined && (
        <Row columnsSize={[12]}>
          <div>
            <Checkbox
              checked={
                // @ts-expect-error
                artist.is_featured !== undefined
                  ? // @ts-expect-error
                    Boolean(artist.is_featured)
                  : false
              }
              disabled={artist.loading}
              label={`Featured ${typeData.value}`}
              onChange={toggleIsFeatured}
            />
          </div>
        </Row>
      )}

      <Row columnsSize={[12]}>
        <div>
          <HTMLEditor
            key={[artist?.loading, artist?.id, artist?.bio].join('-')}
            disabled={artist.loading}
            initialContent={
              artist.bio_editor || getRawContentFromString(artist.bio)
            }
            label={`${typeData.title} bio`}
            placeholder={`Enter ${typeData.value} information, like a bio or...`}
            onChange={(bio_editor, bio_html) =>
              setFormState(state => ({ ...state, bio_editor, bio_html }))
            }
          />
        </div>
      </Row>
      {showcasesEnabled && (
        <Row columnsSize={[12]}>
          <div>
            <Heading size={500}>Social Links</Heading>
            <Button
              appearance="inline-link"
              caption="Manage social links"
              onClick={() => setOpenLinksModal(true)}
            />
          </div>
        </Row>
      )}
      <Row columnsSize={[12]}>
        <div>
          <ButtonContainer>
            <Button
              progressCaption="Saving"
              progressIndicator={saving}
              appearance="primary"
              caption="Save"
              disabled={artist.loading || saving}
              onClick={handleSubmit}
            />
            <Button appearance="link" caption="Cancel" onClick={onCancel} />
          </ButtonContainer>
        </div>
      </Row>
      {openLinksModal && (
        <PlaylistPresentationSocialLinks
          onChange={update => {
            if (typeof update === 'object' && update !== null) {
              setFormState(state => ({
                ...state,
                social_links: { ...state.social_links, ...update },
              }))
            } else {
              setFormState(state => ({
                ...state,
                social_links_aggregator: update,
              }))
            }
          }}
          onClose={() => setOpenLinksModal(false)}
          onSave={() => setOpenLinksModal(false)}
          links={formState.social_links ?? undefined}
          aggregatorLink={formState.social_links_aggregator}
        />
      )}
      {showImageRecommendations && (
        <ImageRecommendationsModal
          onClose={() => setShowImageRecommendations(false)}
        />
      )}
    </>
  )
}

export default ArtistProfile
